/**
 * Grades Service — School-Scoped
 * Manages grade entities stored under schools/{schoolId}/grades
 * Grades are named containers for classes: "Grade 7", "Electives", "Sport", etc.
 */

import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, orderBy, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

class GradesService {
  constructor() {
    this.cache = new Map(); // keyed by schoolId
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get the Firestore collection path for a school's grades
   */
  _collectionPath(schoolId) {
    return `schools/${schoolId}/grades`;
  }

  /**
   * Check if cache is still valid for a school
   */
  _isCacheValid(schoolId) {
    const entry = this.cache.get(schoolId);
    return entry && (Date.now() - entry.timestamp < this.cacheDuration);
  }

  /**
   * Clear the cache for a school (or all)
   */
  clearCache(schoolId = null) {
    if (schoolId) {
      this.cache.delete(schoolId);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get all grades for a school, ordered by 'order' field
   */
  async getAll(schoolId) {
    if (!schoolId) {
      console.warn('[gradesService] getAll called without schoolId');
      return [];
    }

    if (this._isCacheValid(schoolId)) {
      return this.cache.get(schoolId).data;
    }

    try {
      const ref = collection(db, this._collectionPath(schoolId));
      const q = query(ref, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);

      const grades = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      this.cache.set(schoolId, { data: grades, timestamp: Date.now() });
      return grades;
    } catch (error) {
      console.error('[gradesService] Error fetching grades:', error);
      throw error;
    }
  }

  /**
   * Get a single grade by ID
   */
  async getById(schoolId, gradeId) {
    if (!schoolId || !gradeId) return null;

    try {
      const ref = doc(db, this._collectionPath(schoolId), gradeId);
      const snap = await getDoc(ref);
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch (error) {
      console.error(`[gradesService] Error fetching grade ${gradeId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new grade (auto-ID)
   */
  async create(schoolId, gradeData) {
    if (!schoolId) throw new Error('schoolId is required');

    try {
      const ref = collection(db, this._collectionPath(schoolId));
      const now = new Date().toISOString();
      const docRef = await addDoc(ref, {
        ...gradeData,
        isActive: true,
        createdAt: now,
        updatedAt: now
      });

      this.clearCache(schoolId);
      return docRef.id;
    } catch (error) {
      console.error('[gradesService] Error creating grade:', error);
      throw error;
    }
  }

  /**
   * Update an existing grade
   */
  async update(schoolId, gradeId, updates) {
    if (!schoolId || !gradeId) throw new Error('schoolId and gradeId are required');

    try {
      const ref = doc(db, this._collectionPath(schoolId), gradeId);
      await updateDoc(ref, { ...updates, updatedAt: new Date().toISOString() });
      this.clearCache(schoolId);
      return true;
    } catch (error) {
      console.error(`[gradesService] Error updating grade ${gradeId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a grade
   */
  async delete(schoolId, gradeId) {
    if (!schoolId || !gradeId) throw new Error('schoolId and gradeId are required');

    try {
      const ref = doc(db, this._collectionPath(schoolId), gradeId);
      await deleteDoc(ref);
      this.clearCache(schoolId);
      return true;
    } catch (error) {
      console.error(`[gradesService] Error deleting grade ${gradeId}:`, error);
      throw error;
    }
  }

  /**
   * Reorder grades — batch update order fields
   */
  async reorder(schoolId, orderedIds) {
    if (!schoolId) throw new Error('schoolId is required');

    try {
      const batch = writeBatch(db);
      orderedIds.forEach((id, index) => {
        const ref = doc(db, this._collectionPath(schoolId), id);
        batch.update(ref, { order: index, updatedAt: new Date().toISOString() });
      });
      await batch.commit();
      this.clearCache(schoolId);
      return true;
    } catch (error) {
      console.error('[gradesService] Error reordering grades:', error);
      throw error;
    }
  }

  /**
   * Seed grades from an array of objects (migration helper)
   */
  async seedFromArray(schoolId, gradesArray) {
    if (!schoolId) throw new Error('schoolId is required');

    try {
      const batch = writeBatch(db);
      const colRef = collection(db, this._collectionPath(schoolId));
      const now = new Date().toISOString();

      gradesArray.forEach(grade => {
        const docRef = doc(colRef); // auto-ID
        batch.set(docRef, {
          name: grade.name || grade.label || `Grade ${grade.value}`,
          order: grade.order ?? 0,
          isActive: true,
          createdAt: now,
          updatedAt: now
        });
      });

      await batch.commit();
      this.clearCache(schoolId);
      console.log(`[gradesService] Seeded ${gradesArray.length} grades for school ${schoolId}`);
      return true;
    } catch (error) {
      console.error('[gradesService] Error seeding grades:', error);
      throw error;
    }
  }
}

// Export singleton instance
const gradesService = new GradesService();
export default gradesService;
